import { useState } from 'react'
import { api, ApiError, session } from '../lib/api'
import { useApi, useConnection, useGeolocation } from '../lib/hooks'
import type { PhotoRef } from '../lib/photos'
import { photos } from '../lib/photos'
import { PhotoButton } from '../ui/PhotoButton'
import { SignaturePad } from '../ui/SignaturePad'
import { clock, money, num } from '../lib/format'
import {
  Banner, Empty, IconCheck, IconWifiOff, Loading, Panel, Pill, StatMini,
} from '../ui/kit'
import { FieldHeader } from './FieldShell'

/**
 * The driver's day. The last mile is where the margin is won or lost, and the two
 * things that matter happen here: the van departs, and somebody at the door signs
 * for what they actually took.
 *
 * Acceptance is captured at the door, at that moment, per line. A rejection written
 * down later is a rejection nobody can argue about afterwards — and the whole point
 * is that it can be argued about, with evidence, while the driver is still there.
 */

interface Buyer {
  id: string; name: string; address: string | null
  lat: number | null; lng: number | null
  windowStart: string | null; windowEnd: string | null
}

interface Line {
  id: string; lotId: string; lotCode: string
  cropName: string; gradeCode: string
  dispatchedKg: number; acceptedKg: number; rejectedKg: number
  unitPrice: number | null
}

interface Delivery {
  id: string; code: string; seq: number; status: string
  plannedAt: string | null; arrivedAt: string | null; completedAt: string | null
  dispatchedKg: number; acceptedKg: number; rejectedKg: number
  buyer: Buyer; orderCode: string; lines: Line[]
}

interface Trip {
  id: string; code: string; tripDate: string; tripType: string; status: string
  registration: string | null; vehicleLabel: string | null; capacityKg: number | null
  driverName: string | null; corridorName: string | null; depotName: string | null
  plannedKg: number; loadedKg: number; deliveredKg: number; fillPct: number | null
  plannedStops: number; completedStops: number
  startedAt: string | null; completedAt: string | null
}

const REJECT_REASONS = [
  { value: 'quality', label: 'Not the quality ordered' },
  { value: 'spoilage', label: 'Spoiled in transit' },
  { value: 'short_weight', label: 'Short on the scale' },
  { value: 'late', label: 'Arrived outside the window' },
  { value: 'over_order', label: 'More than they ordered' },
  { value: 'no_receiver', label: 'Nobody there to take it' },
  { value: 'wrong_grade', label: 'Wrong grade sent' },
]

export function MyRun() {
  const { online } = useConnection()
  const today = new Date().toISOString().slice(0, 10)

  const run = useApi<Delivery[]>('/deliveries/my-run', { cacheKey: 'run.today' })
  const trips = useApi<Trip[]>(`/trips?date=${today}`, { cacheKey: 'trips.today' })

  const [open, setOpen] = useState<Delivery | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  const stops = run.data ?? []
  const me = session.user
  const mine = (trips.data ?? []).filter((t) => t.driverName === me?.fullName)
  const trip = mine.find((t) => t.status === 'in_transit') ?? mine[0]

  const done = stops.filter((s) => ['delivered', 'part_delivered', 'failed'].includes(s.status))
  const accepted = stops.reduce((a, s) => a + Number(s.acceptedKg ?? 0), 0)
  const rejected = stops.reduce((a, s) => a + Number(s.rejectedKg ?? 0), 0)

  async function depart() {
    if (!trip) return
    if (!window.confirm(`Start your run? The buyers on it will know you are on the way.`)) return
    try {
      await api.post(`/trips/${trip.id}/depart`)
      setBanner({ tone: 'ok', text: 'On the road. Drive safely.' })
      trips.reload(); run.reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'Could not start the trip.' })
    }
  }

  return (
    <>
      <FieldHeader />
      <div className="field-greet">
        <h1>My run</h1>
        <p>
          {trip
            ? <>{trip.code}{trip.registration ? ` · ${trip.registration}` : ''}{trip.corridorName ? ` · ${trip.corridorName}` : ''}</>
            : 'Nothing assigned to you today.'}
        </p>
      </div>

      <div className="field-body">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        )}

        {!online && (
          <Banner tone="warn" icon={<IconWifiOff size={16} />} title="No signal">
            You can still see your run. Getting a signature needs signal — find some before you
            leave, so the buyer cannot argue about it later.
          </Banner>
        )}

        {(run.loading || trips.loading) && <Panel><Loading rows={4} /></Panel>}

        {trip && (
          <div className="brand-card">
            <span className="micro">{trip.status === 'in_transit' ? 'On the road' : 'Ready to go'}</span>
            <div className="bc-title">
              {trip.corridorName ?? trip.depotName ?? trip.code}
              <span>{trip.completedStops} / {trip.plannedStops} stops</span>
            </div>
            <div className="bc-grid">
              <div>
                <b className="num">{num(trip.loadedKg, 0)} kg</b>
                <i>Loaded</i>
              </div>
              <div>
                <b className="num">{trip.fillPct === null ? '—' : `${trip.fillPct}%`}</b>
                <i>Van fill</i>
              </div>
            </div>
            {['planned', 'loading'].includes(trip.status) && (
              <button className="btn block"
                style={{ background: '#fff', color: 'var(--brand-900)', borderColor: '#fff' }}
                onClick={() => void depart()}>
                Start the run
              </button>
            )}
          </div>
        )}

        {!run.loading && stops.length === 0 && (
          <Empty title="No deliveries for you today">
            The office puts together the run and assigns a driver. Until they do, there is nothing
            here for you.
          </Empty>
        )}

        {stops.length > 0 && (
          <>
            <div className="mini-row">
              <StatMini label="Stops" value={`${done.length} / ${stops.length}`} />
              <StatMini label="Accepted" value={`${num(accepted, 0)} kg`} />
              <StatMini label="Rejected" value={`${num(rejected, 0)} kg`}
                note={rejected > 0 ? 'reasons recorded' : 'none'} />
            </div>

            <Panel title="Drops" meta={`${stops.length}`}>
              <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
                {stops.map((s) => {
                  const finished = ['delivered', 'part_delivered', 'failed'].includes(s.status)
                  return (
                    <button key={s.id} className="stop-row" style={{ width: '100%' }}
                      onClick={() => !finished && setOpen(s)}>
                      <span className={`stop-state ${finished ? 'done' : 'pending'}`}>
                        {finished ? '✓' : s.seq}
                      </span>
                      <span className="stop-who">
                        <b>{s.buyer.name}</b>
                        <i>
                          {s.buyer.address ?? s.orderCode}
                          {s.buyer.windowStart && ` · ${clock(s.buyer.windowStart)}–${clock(s.buyer.windowEnd ?? '')}`}
                        </i>
                      </span>
                      <span className="stop-amt">
                        <b>{num(finished ? s.acceptedKg : s.dispatchedKg, 0)} kg</b>
                        <span>
                          {finished
                            ? (Number(s.rejectedKg) > 0
                                ? <Pill tone="warn" dot={false}>{num(s.rejectedKg, 0)} back</Pill>
                                : 'signed')
                            : 'to drop'}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </Panel>
          </>
        )}
      </div>

      {open && (
        <Pod
          delivery={open}
          onClose={() => setOpen(null)}
          onDone={(name) => {
            setOpen(null)
            setBanner({ tone: 'ok', text: `${name} signed for it.` })
            run.reload(); trips.reload()
          }}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------- proof of delivery
function Pod({ delivery, onClose, onDone }: {
  delivery: Delivery
  onClose: () => void
  onDone: (signedName: string) => void
}) {
  const { coords } = useGeolocation()

  const [signedName, setSignedName] = useState('')
  const [signedRole, setSignedRole] = useState('')
  const [temp, setTemp] = useState('')
  const [photo, setPhoto] = useState<PhotoRef | null>(null)
  const [signature, setSignature] = useState<Blob | null>(null)
  const [rejectPhoto, setRejectPhoto] = useState<Record<string, PhotoRef | null>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Everything is accepted until somebody says otherwise. That is the honest default:
  // a driver has to actively record a rejection, not remember to record an acceptance.
  const [rejected, setRejected] = useState<Record<string, string>>({})
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})

  const lines = delivery.lines.map((l) => {
    const rej = Math.min(Number(rejected[l.id]) || 0, Number(l.dispatchedKg))
    return { ...l, rej, acc: Number(l.dispatchedKg) - rej }
  })

  const totalAcc = lines.reduce((s, l) => s + l.acc, 0)
  const totalRej = lines.reduce((s, l) => s + l.rej, 0)
  const value = lines.reduce((s, l) => s + l.acc * Number(l.unitPrice ?? 0), 0)
  const missingReason = lines.some((l) => l.rej > 0 && !reasons[l.id])

  async function sign() {
    if (!signedName.trim()) { setError('Write down the name of whoever signed for it.'); return }
    if (missingReason) { setError('Say why it was sent back. The office will ask the buyer about it.'); return }

    setBusy(true)
    setError(null)
    try {
      // The mark goes up first, the same way a photograph does - and by the same
      // route, so a delivery signed for in a yard with no signal still carries its
      // signature when the queue drains.
      let signatureRef: PhotoRef | null = null
      if (signature) {
        const kept = await photos.keep(signature, 'signature', coords)
        signatureRef = kept.ref
      }

      await api.post(`/deliveries/${delivery.id}/pod`, {
        clientUuid: crypto.randomUUID(),
        signedName: signedName.trim(),
        signedRole: signedRole.trim() || null,
        signatureUrl: signatureRef,
        photoUrl: photo,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        temperatureC: temp ? Number(temp) : null,
        lines: lines.map((l) => ({
          deliveryLineId: l.id,
          acceptedKg: l.acc,
          rejectedKg: l.rej,
          rejectReasonCode: l.rej > 0 ? reasons[l.id] : null,
          rejectNote: l.rej > 0 ? (notes[l.id] || null) : null,
          rejectPhotoUrl: l.rej > 0 ? (rejectPhoto[l.id] ?? null) : null,
        })),
      })
      onDone(signedName.trim())
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="drawer-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="drawer">
        <header className="drawer-head">
          <div>
            <span className="micro">Stop {delivery.seq} · {delivery.code}</span>
            <h3>{delivery.buyer.name}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="drawer-body">
          {error && <div className="error-text" style={{ marginBottom: 12 }}>{error}</div>}

          <div className="micro" style={{ marginBottom: 'var(--sp-4)' }}>
            What you are dropping here
          </div>

          {lines.map((l) => (
            <div key={l.id} style={{
              border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
              padding: 'var(--sp-5) var(--sp-6)', marginBottom: 'var(--sp-5)',
            }}>
              <div className="spread">
                <div>
                  <b>{l.cropName} <Pill dot={false}>{l.gradeCode}</Pill></b>
                  <div className="faint code" style={{ fontSize: 'var(--fs-xs)' }}>{l.lotCode}</div>
                </div>
                <b className="num">{num(l.dispatchedKg, 1)} kg</b>
              </div>

              <div className="field" style={{ marginTop: 'var(--sp-5)', marginBottom: 0 }}>
                <label htmlFor={`rej-${l.id}`}>Sent back</label>
                <input id={`rej-${l.id}`} className="input n" type="number" step="0.1"
                  min="0" max={l.dispatchedKg} inputMode="decimal" placeholder="0"
                  value={rejected[l.id] ?? ''}
                  onChange={(e) => setRejected((s) => ({ ...s, [l.id]: e.target.value }))} />
              </div>

              {l.rej > 0 && (
                <>
                  <div className="field" style={{ marginTop: 'var(--sp-5)', marginBottom: 0 }}>
                    <label htmlFor={`why-${l.id}`}>
                      Why<span className="crit-t"> *</span>
                    </label>
                    <select id={`why-${l.id}`} className="input" value={reasons[l.id] ?? ''}
                      onChange={(e) => setReasons((s) => ({ ...s, [l.id]: e.target.value }))}>
                      <option value="">— choose —</option>
                      {REJECT_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ marginTop: 'var(--sp-4)', marginBottom: 0 }}>
                    <input className="input" placeholder="What exactly? (optional)"
                      value={notes[l.id] ?? ''}
                      onChange={(e) => setNotes((s) => ({ ...s, [l.id]: e.target.value }))} />
                  </div>

                  {/* A rejection you cannot show anybody is a rejection you lose. */}
                  <div style={{ marginTop: 'var(--sp-4)' }}>
                    <PhotoButton
                      kind="rejection"
                      where={coords}
                      value={rejectPhoto[l.id] ?? null}
                      onChange={(ref) => setRejectPhoto((s) => ({ ...s, [l.id]: ref }))}
                      label="Photograph what they turned away"
                    />
                  </div>
                  <p className="hint" style={{ marginTop: 'var(--sp-4)' }}>
                    Taking {num(l.acc, 1)} kg of {num(l.dispatchedKg, 1)} kg.
                  </p>
                </>
              )}
            </div>
          ))}

          <div className="divider" style={{ margin: 'var(--sp-6) 0' }} />

          <div className="field">
            <label htmlFor="pod-name">Who signed<span className="crit-t"> *</span></label>
            <input id="pod-name" className="input" value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder="Their name, as they give it" />
          </div>

          <div className="field">
            <label htmlFor="pod-role">What they do there</label>
            <input id="pod-role" className="input" value={signedRole}
              onChange={(e) => setSignedRole(e.target.value)}
              placeholder="Storeman, chef, owner…" />
          </div>

          <div className="field">
            <SignaturePad onChange={setSignature} disabled={busy} />
          </div>

          <PhotoButton
            kind="pod"
            where={coords}
            value={photo}
            onChange={setPhoto}
            label="Photograph the delivery"
          />

          <div className="field">
            <label htmlFor="pod-temp">Temperature on arrival</label>
            <input id="pod-temp" className="input n" type="number" step="0.1" inputMode="decimal"
              placeholder="°C" value={temp} onChange={(e) => setTemp(e.target.value)} />
          </div>

          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--line-2)',
            borderRadius: 'var(--r-md)', padding: 'var(--sp-5) var(--sp-6)',
            fontSize: 'var(--fs-sm)',
          }}>
            <div className="spread">
              <span className="muted">Accepted</span>
              <b className="num">{num(totalAcc, 1)} kg</b>
            </div>
            {totalRej > 0 && (
              <div className="spread">
                <span className="muted">Going back with you</span>
                <b className="num crit-t">{num(totalRej, 1)} kg</b>
              </div>
            )}
            <div className="spread">
              <span className="muted">Invoiced</span>
              <b className="num">{money(value)}</b>
            </div>
            <p className="hint" style={{ marginTop: 'var(--sp-4)' }}>
              {signature
                ? (coords
                    ? 'Signed, and where you are is saved with it.'
                    : 'Signed. No GPS yet, so it is saved without a location.')
                : 'Nobody has signed yet. A typed name is the driver’s word for who took it; the mark is theirs.'}
            </p>
          </div>
        </div>

        <footer className="drawer-foot">
          <button type="button" className="btn quiet" onClick={onClose}>Not yet</button>
          <button className="btn" disabled={busy || !signedName.trim()} onClick={() => void sign()}>
            {busy ? 'Saving…' : <><IconCheck size={15} /> Signed for</>}
          </button>
        </footer>
      </div>
    </div>
  )
}
